import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import TimelineChart from "@/components/TimelineChart";
import CalendarView from "@/components/CalendarView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtDate } from "@/lib/date";
import {
  IdentificationCard,
  Airplane,
  BookOpenText,
  Cake,
  GlobeStand,
  Checks,
  GraduationCap,
  CurrencyDollar,
  Receipt,
  ArrowsDownUp,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";

const badgeForDays = (days) => {
  if (days == null) return "";
  if (days <= 30) return "text-rose-500 dark:text-rose-400";
  if (days <= 90) return "text-amber-600 dark:text-amber-400";
  return "text-emerald-600 dark:text-emerald-400";
};

const Card = ({ title, icon: Icon, items, sortDir, onToggleSort, dateLabel, testid }) => {
  const sorted = useMemo(() => {
    const a = [...items];
    a.sort((x, y) => (sortDir === "asc" ? x.date.localeCompare(y.date) : y.date.localeCompare(x.date)));
    return a;
  }, [items, sortDir]);

  return (
    <div
      data-testid={testid}
      className="bg-card border border-black/5 dark:border-white/10 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-transform duration-200 hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#007AFF]/10 text-[#007AFF] flex items-center justify-center">
            <Icon size={18} weight="duotone" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Top 10</p>
            <h3 className="font-heading font-semibold text-lg">{title}</h3>
          </div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={onToggleSort}
              data-testid={`${testid}-sort`}
            >
              <ArrowsDownUp size={16} weight="duotone" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sort by {dateLabel} ({sortDir})</TooltipContent>
        </Tooltip>
      </div>

      {sorted.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">No upcoming records.</div>
      ) : (
        <ul className="divide-y divide-black/5 dark:divide-white/10 stagger-in">
          {sorted.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-2.5">
              <span className="text-sm font-medium truncate pr-3">{r.name}</span>
              <span className={`text-sm tabular-nums ${badgeForDays(r.days_left)}`}>
                {fmtDate(r.date)} <span className="text-muted-foreground text-xs ml-1">({r.days_left}d)</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CommissionCard = ({ data }) => {
  const items = data?.items || [];
  const pending = data?.pending_this_month ?? 0;
  const overdue = data?.overdue ?? 0;
  const monthLabel = data?.month_label || "this month";
  return (
    <div
      data-testid="dashboard-card-commission"
      className="bg-card border border-black/5 dark:border-white/10 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] transition-transform duration-200 hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-500/10 text-violet-600 dark:text-violet-400 flex items-center justify-center">
            <Receipt size={18} weight="duotone" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Finance</p>
            <h3 className="font-heading font-semibold text-lg">Commissions</h3>
          </div>
        </div>
        <Link
          to="/reports/commissions"
          data-testid="dashboard-commission-report-link"
          className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
        >
          Report →
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div
          data-testid="dashboard-commission-pending"
          className="rounded-xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-4 py-3"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Pending · {monthLabel}</p>
          <p className="font-heading text-3xl font-semibold tabular-nums mt-1">{pending}</p>
        </div>
        <div
          data-testid="dashboard-commission-overdue"
          className={`rounded-xl border px-4 py-3 ${
            overdue > 0
              ? "border-rose-500/30 bg-rose-500/5"
              : "border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5"
          }`}
        >
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1">
            {overdue > 0 && <WarningCircle size={11} weight="fill" className="text-rose-500" />}
            Overdue
          </p>
          <p className={`font-heading text-3xl font-semibold tabular-nums mt-1 ${overdue > 0 ? "text-rose-600 dark:text-rose-400" : ""}`}>
            {overdue}
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No upcoming commissions.</div>
      ) : (
        <ul className="divide-y divide-black/5 dark:divide-white/10 stagger-in">
          {items.map((r) => (
            <li key={`${r.id}-${r.date}`} className="flex items-center justify-between py-2.5 gap-3">
              <div className="min-w-0">
                <Link to={`/clients/${r.id}`} className="text-sm font-medium truncate block hover:underline">
                  {r.name}
                </Link>
                {(r.institution || r.term) && (
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[r.institution, r.term].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <span className={`text-sm tabular-nums whitespace-nowrap ${badgeForDays(r.days_left)}`}>
                {fmtDate(r.date)} <span className="text-muted-foreground text-xs ml-1">({r.days_left}d)</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [sortDirs, setSortDirs] = useState({
    passport: "asc", visa: "asc", english_test: "asc",
    naati: "asc", skill_assessment: "asc", py: "asc",
    tuition_fee: "asc", birthday: "asc",
  });

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
  }, []);

  const toggle = (k) => setSortDirs((s) => ({ ...s, [k]: s[k] === "asc" ? "desc" : "asc" }));

  if (!data) {
    return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Overview
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            Welcome to {data.company_name}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            Snapshot of expiring documents and upcoming birthdays. Red = ≤30 days, Amber = ≤90 days.
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="dashboard-tabs">
          <TabsTrigger value="overview" data-testid="dashboard-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="dashboard-tab-calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-8">
          <TimelineChart />

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="dashboard-grid">
        <Card
          testid="dashboard-card-passport"
          title="Passport expiring"
          icon={IdentificationCard}
          items={data.passport}
          sortDir={sortDirs.passport}
          onToggleSort={() => toggle("passport")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-visa"
          title="Visa expiring"
          icon={Airplane}
          items={data.visa}
          sortDir={sortDirs.visa}
          onToggleSort={() => toggle("visa")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-english"
          title="IELTS / PTE expiring"
          icon={BookOpenText}
          items={data.english_test}
          sortDir={sortDirs.english_test}
          onToggleSort={() => toggle("english_test")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-naati"
          title="NAATI expiring"
          icon={GlobeStand}
          items={data.naati}
          sortDir={sortDirs.naati}
          onToggleSort={() => toggle("naati")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-skill"
          title="Skill Assessment expiring"
          icon={Checks}
          items={data.skill_assessment}
          sortDir={sortDirs.skill_assessment}
          onToggleSort={() => toggle("skill_assessment")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-py"
          title="PY expiring"
          icon={GraduationCap}
          items={data.py}
          sortDir={sortDirs.py}
          onToggleSort={() => toggle("py")}
          dateLabel="expiration"
        />
        <Card
          testid="dashboard-card-tuition"
          title="Tuition fees due"
          icon={CurrencyDollar}
          items={data.tuition_fee}
          sortDir={sortDirs.tuition_fee}
          onToggleSort={() => toggle("tuition_fee")}
          dateLabel="due"
        />
        <Card
          testid="dashboard-card-birthday"
          title="Upcoming birthdays"
          icon={Cake}
          items={data.birthday}
          sortDir={sortDirs.birthday}
          onToggleSort={() => toggle("birthday")}
          dateLabel="birthday"
        />
        <CommissionCard data={data.commission} />
      </div>
        </TabsContent>

        <TabsContent value="calendar">
          <CalendarView />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;
